import { gql, useQuery } from '@apollo/client';
import _ from 'lodash';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useAppSelector from '../app/redux/useAppSelector';
import EmptyState from '../components/EmptyState';
import type { BrandAndProductProps } from '../components/ExternalBrandLogo';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import type {
  ExternalPostsQuery,
  ExternalPostsQueryVariables,
} from '../graphql/generated/graphqlTypes';
import {
  ExternalPostsFilter,
  ExternalPostsOrder,
} from '../graphql/generated/graphqlTypes';
import allPostsIcon from '../icons/allPosts.svg';
import archiveIcon from '../icons/archive.svg';
import orderIcon from '../icons/order.svg';
import postIcon from '../icons/post.svg';
import templateIcon from '../icons/template.svg';
import verifiedIcon from '../icons/verified.svg';
import UIActions from '../ui/UIActions';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import PageMeta from '../utils/PageMeta';
import { externalDisplayName } from '../utils/post/externalPost';
import styles from './ExternalPostsView.module.scss';
import PostFilterMenu from './PostFilterMenu';
import type { MetadataAttribute } from './PostRow';
import PostRow from './PostRow';
import PostSectionHeader from './PostSectionHeader';

const METADATA_ATTRIBUTE: Record<ExternalPostsOrder, MetadataAttribute> = {
  [ExternalPostsOrder.Popularity]: 'popularity',
  [ExternalPostsOrder.RecentlyCreated]: 'publishedAt',
  [ExternalPostsOrder.RecentlyUpdated]: 'updatedAt',
  [ExternalPostsOrder.Alphabetical]: 'updatedAt',
  [ExternalPostsOrder.RecentlyViewed]: 'userViewedAt',
};

const EXTERNAL_POSTS_QUERY = gql`
  query externalPosts(
    $id: String!
    $orderBy: ExternalPostsOrder
    $filter: ExternalPostsFilter
    $afterCursor: String
  ) {
    platform: externalPostPlatform(id: $id) {
      id
      allPostsCount: postsCount(filter: ALL)
      verifiedPostsCount: postsCount(filter: VERIFIED)
      publishedPostsCount: postsCount(filter: PUBLISHED)
      archivedPostsCount: postsCount(filter: ARCHIVED)
      templatePostsCount: postsCount(filter: TEMPLATE)
      posts(
        after: $afterCursor
        first: 25
        orderBy: $orderBy
        filter: $filter
      ) {
        edges {
          node {
            ...PostRow_post
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${PostRow.fragments.post}
`;

const ExternalPostsView = () => {
  const dispatch = useDispatch();
  const { platform: platformId } = useParams() as { platform: string };
  const [type, setType] = useState(ExternalPostsFilter.Published);
  const orderBy =
    useAppSelector((state) => state.ui.externalPostsOrder) ||
    ExternalPostsOrder.Popularity;

  const [activePreviewId, setActivePreviewId] = useState<string | null>(null);

  const response = useQuery<ExternalPostsQuery, ExternalPostsQueryVariables>(
    EXTERNAL_POSTS_QUERY,
    { variables: { id: platformId, orderBy, filter: type } },
  );

  const fetchMore = ConnectionHelper.generateFetchMore(
    EXTERNAL_POSTS_QUERY,
    response,
    'platform.posts',
  );

  const platform = response.data?.platform;

  if (!platform) {
    return null;
  }

  const { id } = platform;
  const displayName = externalDisplayName(id);
  const docTitle = displayName ? `${displayName} - Slab` : 'Slab';

  const posts = _.compact(platform.posts?.edges?.map((edge) => edge?.node));

  const counts: Record<ExternalPostsFilter, number> = {
    [ExternalPostsFilter.All]: platform.allPostsCount,
    [ExternalPostsFilter.Archived]: platform.archivedPostsCount,
    [ExternalPostsFilter.Verified]: platform.verifiedPostsCount,
    [ExternalPostsFilter.Published]: platform.publishedPostsCount,
    [ExternalPostsFilter.Template]: platform.templatePostsCount,
  };

  return (
    <div id="content" className="no-padding-x no-padding-y">
      <PageMeta title={docTitle} />
      <div className={styles.container}>
        <PageContent>
          <div>
            <PageTitle
              title={
                <h1 className={styles.mainTitle}>
                  <div className={styles.titleLogo}>
                    <ExternalBrandLogo
                      {...({ brand: id } as BrandAndProductProps)}
                      height={32}
                    />
                  </div>
                  {displayName}
                </h1>
              }
              subtitle={`These posts are synced from Markdown files in your ${displayName} repositories.`}
            />
            <PostSectionHeader
              controls={
                <>
                  <PostFilterMenu
                    onChange={setType}
                    options={{
                      [ExternalPostsFilter.Published]: {
                        icon: postIcon,
                        count: counts.PUBLISHED,
                        label: 'Published',
                      },
                      [ExternalPostsFilter.Verified]: {
                        icon: verifiedIcon,
                        count: counts.VERIFIED,
                        label: 'Verified',
                      },
                      [ExternalPostsFilter.Template]: {
                        icon: templateIcon,
                        count: counts.TEMPLATE,
                        label: 'Template',
                      },
                      [ExternalPostsFilter.Archived]: {
                        icon: archiveIcon,
                        count: counts.ARCHIVED,
                        label: 'Archived',
                      },
                      [ExternalPostsFilter.All]: {
                        icon: allPostsIcon,
                        count: counts.ALL,
                        label: 'All posts',
                      },
                    }}
                    title="Show"
                    value={type}
                  />
                  <PostFilterMenu
                    icon={orderIcon}
                    onChange={(value) =>
                      dispatch(UIActions.setExternalPostsOrder(value))
                    }
                    options={{
                      [ExternalPostsOrder.Popularity]: { label: 'Popularity' },
                      [ExternalPostsOrder.RecentlyCreated]: {
                        label: 'Recently created',
                      },
                      [ExternalPostsOrder.RecentlyUpdated]: {
                        label: 'Recently updated',
                      },
                      [ExternalPostsOrder.RecentlyViewed]: {
                        label: 'Recently viewed',
                      },
                      [ExternalPostsOrder.Alphabetical]: {
                        label: 'Alphabetical',
                      },
                    }}
                    menuPlacement="bottom-end"
                    title="Sort"
                    value={orderBy}
                  />
                </>
              }
              fullPageWidth={false}
            >
              <h3>Synced posts</h3>
            </PostSectionHeader>
            {posts.length ? (
              <>
                {posts.map((post, index) => (
                  <PostRow
                    key={post.id}
                    post={post}
                    inList={index > 0}
                    metadataAttribute={METADATA_ATTRIBUTE[orderBy]}
                    activePreviewId={activePreviewId}
                    onActivePreviewIdChange={setActivePreviewId}
                  />
                ))}
              </>
            ) : (
              <EmptyState
                imageName="notFound"
                title="No posts found"
                message={`We couldn't find viewable posts synced from ${displayName}.`}
              />
            )}
          </div>

          <InfiniteScrollWaypoint fetchMore={fetchMore} />
        </PageContent>
      </div>
    </div>
  );
};

export default ExternalPostsView;
